import { useTranslation } from 'react-i18next';
import {
  CardGiftcard,
  ForwardToInbox,
  CheckCircleOutline,
  Gavel,
  Link as LinkIcon,
  LinkOff,
  Payments,
  Refresh,
  StopCircle,
} from '@mui/icons-material';
import MenuMoreVert from '../../../components/MenuMoreVert';
import { PERMISSIONS } from '../../../consts/permissions';
import { MenuMoreVertPosition } from '../../../consts/enums';
import { ReferralRowAction } from '../../../enums/referral.enum';
import { useAuth } from '../../../hooks/useAuth';
import STRING_KEYS from '../../../language/keys';
import { Referral } from '../../../@types/Referral';
import {
  canForceSendReward, canOverrideEligibility, canQualify, canResendReward, getOpenPositions, isReissueReward,
} from '../referrals.utils';
import { PendingReferralAction } from '../referrals.types';

type MenuAction = {
  key: string,
  type: ReferralRowAction,
  title: string,
  icon: JSX.Element,
};

type ReferralRowActionsProps = {
  referral: Referral,
  onAction: (action: PendingReferralAction) => void,
};

export const ReferralRowActions = ({ referral, onAction }: ReferralRowActionsProps): JSX.Element | null => {
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();
  const canEdit = hasPermissions([PERMISSIONS.REFERRAL_EDIT]);
  const canManageRewards = hasPermissions([PERMISSIONS.REFERRAL_MANAGE_REWARDS]);

  const salesActions: MenuAction[] = [];
  if (canOverrideEligibility(referral)) {
    salesActions.push({
      key: referral.id,
      type: ReferralRowAction.OverrideEligibility,
      title: translate(STRING_KEYS.REFERRALS.ACTION_OVERRIDE_ELIGIBILITY),
      icon: <Gavel />,
    });
  }
  salesActions.push({
    key: referral.id,
    type: ReferralRowAction.RecheckEligibility,
    title: translate(STRING_KEYS.REFERRALS.ACTION_RECHECK_ELIGIBILITY),
    icon: <Refresh />,
  });
  salesActions.push({
    key: referral.id,
    type: ReferralRowAction.LinkAccount,
    title: translate(referral.referredAccountId ?
      STRING_KEYS.REFERRALS.ACTION_CHANGE_LINKED_ACCOUNT :
      STRING_KEYS.REFERRALS.ACTION_LINK_ACCOUNT),
    icon: <LinkIcon />,
  });
  if (referral.referredAccountId) {
    salesActions.push({
      key: referral.id,
      type: ReferralRowAction.UnlinkAccount,
      title: translate(STRING_KEYS.REFERRALS.ACTION_UNLINK_ACCOUNT),
      icon: <LinkOff />,
    });
  }
  salesActions.push({
    key: referral.id,
    type: ReferralRowAction.TogglePayingOutsideStripe,
    title: translate(referral.payingOutsideStripe ?
      STRING_KEYS.REFERRALS.ACTION_CLEAR_PAYING_OUTSIDE_STRIPE :
      STRING_KEYS.REFERRALS.ACTION_SET_PAYING_OUTSIDE_STRIPE),
    icon: <Payments />,
  });
  if (canQualify(referral)) {
    salesActions.push({
      key: referral.id,
      type: ReferralRowAction.Qualify,
      title: translate(STRING_KEYS.REFERRALS.ACTION_QUALIFY),
      icon: <CheckCircleOutline />,
    });
  }

  const rewardActions: MenuAction[] = [
    ...(referral.rewards || []).filter(canResendReward).map((reward) => ({
      key: reward.id,
      type: ReferralRowAction.ResendReward,
      title: translate(
        isReissueReward(reward) ? STRING_KEYS.REFERRALS.ACTION_REISSUE_REWARD : STRING_KEYS.REFERRALS.ACTION_RESEND_REWARD,
        { milestone: reward.milestone },
      ),
      icon: <CardGiftcard />,
    })),
    ...(referral.rewards || []).filter(canForceSendReward).map((reward) => ({
      key: reward.id,
      type: ReferralRowAction.ForceSendReward,
      title: translate(STRING_KEYS.REFERRALS.ACTION_FORCE_SEND_REWARD, { milestone: reward.milestone }),
      icon: <ForwardToInbox />,
    })),
    ...getOpenPositions(referral).map((position) => ({
      key: position.id,
      type: ReferralRowAction.EndPosition,
      title: translate(STRING_KEYS.REFERRALS.ACTION_END_POSITION),
      icon: <StopCircle />,
    })),
  ];

  const actions: MenuAction[] = [
    ...(canEdit ? salesActions : []),
    ...(canManageRewards ? rewardActions : []),
  ];

  if (!actions.length) {
    return null;
  }

  const handleActions = (targetId: string, type: string) => {
    const action = actions.find((menuAction) => menuAction.key === targetId && menuAction.type === type);
    if (action) {
      onAction({ type: action.type, referral, targetId });
    }
  };

  return (
    <MenuMoreVert
      actions={ actions }
      handleActions={ handleActions }
      position={ MenuMoreVertPosition.button }
    />
  );
};
